/**
 * DỰ ÁN — một việc lớn được giao cho vài người, gom mọi tài nguyên để làm nó: thumbnail (ảnh), nhạc
 * trong Kho nhạc, video đã đăng, link ngoài; kèm danh sách việc nhỏ có người làm và ô tick.
 *
 * Bản ghi `project` DÙNG CHUNG (không «có chủ»): máy chủ gửi cho mọi người, giao diện lọc theo luật
 * `canSeeProject` — người tạo, người được giao, và cấp trên nhìn được họ. Ảnh là bản ghi `projimg` riêng
 * (mỗi ảnh một bản ghi, ảnh nằm ở khe `thumb`), nối bằng `pj` = id dự án.
 */
import type { RecordDto } from '@halo/shared';
import {
  dateOrdinal,
  daysBetween,
  formatFullDate,
  parseFullDate,
  type CalendarDate,
} from '../dates';
import { normalizeForSearch } from '../format';
import { describeUploadLimit } from '../music';
import {
  applyChanges,
  compactData,
  readNumber,
  readString,
  readStringList,
  type RecordData,
} from './parsing';

export type ProjectStatus = 'todo' | 'doing' | 'done';
export const PROJECT_STATUSES: readonly ProjectStatus[] = ['todo', 'doing', 'done'];
export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  todo: 'Chờ làm',
  doing: 'Đang làm',
  done: 'Xong',
};

export interface ProjectTask {
  id: string;
  text: string;
  /** Tên đăng nhập người làm; '' = chưa giao. */
  who: string;
  done: boolean;
}

export interface ProjectLink {
  label: string;
  url: string;
}

export interface Project {
  recordId: number;
  title: string;
  description: string;
  status: ProjectStatus;
  /** Ngày bắt đầu thực hiện (khoá `sd`); null = chưa đặt. Cùng `deadline` là «thời gian thực hiện». */
  startDate: CalendarDate | null;
  deadline: CalendarDate | null;
  /** Kênh đích (mã kênh của mình), '' = chưa gắn. */
  channelCode: string;
  createdBy: string;
  /** ISO. */
  createdAt: string;
  /** Mã dự án tự đặt («2026SP029»), '' = không có — thêm 22/09/2026 cùng phần tổng kho. */
  code: string;
  /** Người phụ trách (tên đăng nhập); '' = chưa gán → giao diện coi là người tạo. */
  lead: string;
  /** TỒN BAN ĐẦU: số video / thumbnail có sẵn khi mở dự án. Kho hiện tại = tồn ban đầu + sản lượng theo tuần. */
  initialVideo: number;
  initialThumb: number;
  /** GIỜ / SẢN PHẨM — thời gian làm một sản phẩm của dự án (KPI: giờ được giao = KPI tuần × giờ/SP); 0 = chưa đặt. */
  hoursPerUnit: number;
  /**
   * GHI CHÚ ngắn của dự án (02/10/2026, chủ dự án: «SP/giờ chuyển sang Ghi chú dạng text») — ô chữ sửa tại
   * chỗ trên bảng Dự án của KPI, thay cho ô giờ/SP (giờ/SP vẫn nhập được ở form đầy đủ). Khác «mô tả».
   */
  note: string;
  assignees: string[];
  tasks: ProjectTask[];
  /** Mã bài trong Kho nhạc. */
  trackIds: string[];
  /** Id bản ghi video (mục SEO kênh). */
  videoIds: number[];
  links: ProjectLink[];
}

export interface ProjectImage {
  recordId: number;
  /** Người đưa ảnh lên (chủ bản ghi); null = bản ghi cũ không rõ ai. */
  owner: string | null;
  projectId: number;
  caption: string;
  /**
   * NGÀY của ảnh «DD/MM/YYYY» (thêm 25/09/2026, khoá `d`) — thumbnail làm cho dự án nào, hôm nào.
   * Ảnh cũ chưa có ngày thì để trống: không đoán bừa theo ngày tạo bản ghi, vì ảnh dán bù cho hôm
   * trước là chuyện thường.
   */
  dateKey: string;
  /** Folder trong dự án (id bản ghi `projfd`); null = chưa xếp folder. */
  folderId: number | null;
  /** Đường dẫn ảnh do máy chủ cấp; null = bản ghi thiếu ảnh. */
  url: string | null;
}

/**
 * FOLDER TRONG MỘT DỰ ÁN (25/09/2026) — ngăn chứa ảnh và file gốc của thumbnail: «Bản nháp»,
 * «Đã duyệt», hay theo từng kênh. Chỉ có tên và dự án chủ quản; thứ gì nằm trong folder thì chính
 * thứ ấy trỏ ngược lại (`fd`), nên xoá folder không kéo theo ảnh nào.
 */
export interface ProjectFolder {
  recordId: number;
  projectId: number;
  name: string;
}

// ---------------------------------------------------------------- đọc / ghi

function readStatus(value: unknown): ProjectStatus {
  return value === 'doing' || value === 'done' ? value : 'todo';
}

function readTasks(value: unknown): ProjectTask[] {
  if (!Array.isArray(value)) return [];
  const tasks: ProjectTask[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const data = item as RecordData;
    const text = readString(data, 'text').trim();
    if (!text) continue;
    tasks.push({
      id: readString(data, 'id') || `t${tasks.length + 1}`,
      text,
      who: readString(data, 'who'),
      done: data.done === true || data.done === 1,
    });
  }
  return tasks;
}

function readLinks(value: unknown): ProjectLink[] {
  if (!Array.isArray(value)) return [];
  const links: ProjectLink[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const data = item as RecordData;
    const url = readString(data, 'url').trim();
    if (!url) continue;
    links.push({ label: readString(data, 'label').trim(), url });
  }
  return links;
}

/** Số lượng trong kho: nguyên, không âm; thiếu hay lạ = 0. */
export function readStock(data: RecordData, key: string): number {
  const value = readNumber(data, key);
  return value === null ? 0 : Math.max(0, Math.round(value));
}

function readNumberList(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((item): item is number => typeof item === 'number' && Number.isInteger(item))
    : [];
}

export function parseProject(record: RecordDto): Project {
  const data = record.data;
  return {
    recordId: record.id,
    title: readString(data, 'title'),
    description: readString(data, 'desc'),
    status: readStatus(data.st),
    startDate: parseFullDate(readString(data, 'sd')),
    deadline: parseFullDate(readString(data, 'dl')),
    channelCode: readString(data, 'ch'),
    createdBy: readString(data, 'by'),
    createdAt: readString(data, 'at'),
    code: readString(data, 'code').trim(),
    lead: readString(data, 'lead'),
    initialVideo: readStock(data, 'iv'),
    initialThumb: readStock(data, 'it'),
    hoursPerUnit: Math.max(0, readNumber(data, 'hpu') ?? 0),
    note: readString(data, 'note'),
    assignees: [...new Set(readStringList(data, 'asg'))],
    tasks: readTasks(data.tasks),
    trackIds: [...new Set(readStringList(data, 'tracks'))],
    videoIds: [...new Set(readNumberList(data.videos))],
    links: readLinks(data.links),
  };
}

function toProjectData(project: Project): RecordData {
  return {
    title: project.title,
    desc: project.description,
    st: project.status,
    sd: project.startDate ? formatFullDate(project.startDate) : '',
    dl: project.deadline ? formatFullDate(project.deadline) : '',
    ch: project.channelCode,
    by: project.createdBy,
    at: project.createdAt,
    code: project.code,
    lead: project.lead,
    iv: project.initialVideo,
    it: project.initialThumb,
    hpu: project.hoursPerUnit,
    note: project.note,
    asg: project.assignees,
    tasks: project.tasks.map((task) => ({
      id: task.id,
      text: task.text,
      who: task.who,
      done: task.done,
    })),
    tracks: project.trackIds,
    videos: project.videoIds,
    links: project.links.map((link) => ({ label: link.label, url: link.url })),
  };
}

/** Có `stored` (đang SỬA): chỉ ghi đè khoá thật sự đổi — cùng luật với mọi bản ghi khác. */
export function serializeProject(project: Project, stored?: RecordDto): RecordData {
  const next = toProjectData(project);
  if (!stored) return compactData(next);
  return compactData(applyChanges(stored.data, toProjectData(parseProject(stored)), next));
}

export function parseProjectImage(record: RecordDto): ProjectImage {
  return {
    recordId: record.id,
    owner: record.owner,
    projectId: readNumber(record.data, 'pj') ?? 0,
    caption: readString(record.data, 'cap'),
    dateKey: readString(record.data, 'd'),
    folderId: readNumber(record.data, 'fd') ?? null,
    url: record.images.thumb ?? null,
  };
}

export function serializeProjectImage(
  projectId: number,
  caption: string,
  dateKey: string,
  folderId: number | null = null,
): RecordData {
  return compactData({
    pj: projectId,
    cap: caption || undefined,
    d: dateKey || undefined,
    fd: folderId ?? undefined,
  });
}

export function parseProjectFolder(record: RecordDto): ProjectFolder {
  return {
    recordId: record.id,
    projectId: readNumber(record.data, 'pj') ?? 0,
    name: readString(record.data, 'name'),
  };
}

export function serializeProjectFolder(projectId: number, name: string): RecordData {
  return { pj: projectId, name: name.trim() };
}

/** Folder của một dự án, xếp theo tên. Tên trùng nhau thì bản ghi cũ trước — không giấu cái nào. */
export function foldersOfProject(
  folders: readonly ProjectFolder[],
  projectId: number,
): ProjectFolder[] {
  return folders
    .filter((folder) => folder.projectId === projectId)
    .sort((a, b) => a.name.localeCompare(b.name, 'vi') || a.recordId - b.recordId);
}

/** Lý do chưa tạo được folder, hoặc null. Trùng tên TRONG CÙNG dự án là không cho. */
export function folderProblem(
  name: string,
  folders: readonly ProjectFolder[],
  projectId: number,
): string | null {
  const wanted = normalizeForSearch(name);
  if (!wanted) return 'Đặt tên cho folder đã';
  const clash = foldersOfProject(folders, projectId).some(
    (folder) => normalizeForSearch(folder.name) === wanted,
  );
  return clash ? 'Dự án này đã có folder tên đó' : null;
}

// ---------------------------------------------------------------- Kho thumbnail

/**
 * Đuôi file mà TRÌNH DUYỆT vẽ ra được — chỉ những file này mới đi đường «ảnh xem trước» (thu nhỏ
 * còn 640px rồi lưu vào bản ghi ảnh).
 */
const PREVIEW_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'bmp'];

/**
 * File này là ẢNH XEM TRƯỚC hay FILE GỐC? Xét theo ĐUÔI FILE, không theo kiểu MIME (sửa
 * 26/09/2026).
 *
 * Windows khai .psd là «image/vnd.adobe.photoshop», nên luật cũ («type bắt đầu bằng image/») đẩy
 * PSD sang đường thu nhỏ ảnh — trình duyệt không vẽ được PSD, và PSD thường nặng hơn trần 15 MB
 * của bước thu nhỏ, nên người dùng chỉ thấy «Ảnh lớn hơn 15 MB» và KHÔNG tải lên được file gốc.
 */
export function isPreviewImageFile(fileName: string): boolean {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0) return false;
  return PREVIEW_EXTENSIONS.includes(fileName.slice(dot + 1).toLowerCase());
}

/**
 * Một thứ xếp được vào Kho thumbnail: ảnh xem trước HOẶC file gốc (PSD…). Hai loại nằm ở hai chỗ
 * lưu khác nhau (bản ghi ảnh / kho tệp trên đĩa) nhưng xếp chung một lưới, nên gom nhóm chỉ cần
 * đúng hai trường này.
 */
export interface ThumbnailItem {
  projectId: number;
  /** «DD/MM/YYYY»; chuỗi rỗng = chưa ghi ngày. */
  dateKey: string;
}

/** Những thứ của MỘT ngày trong một dự án. */
export interface ThumbnailDay<T extends ThumbnailItem> {
  dateKey: string;
  items: T[];
}

export interface ThumbnailProjectDays<T extends ThumbnailItem> {
  projectId: number;
  days: ThumbnailDay<T>[];
  total: number;
}

/** Ngày mới nhất trước; ảnh chưa ghi ngày xuống cuối cùng. */
function byDayDesc(a: string, b: string): number {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const left = parseFullDate(a);
  const right = parseFullDate(b);
  return (right ? dateOrdinal(right) : 0) - (left ? dateOrdinal(left) : 0);
}

/**
 * KHO THUMBNAIL (25/09/2026) — gom ảnh theo NGÀY, trong mỗi ngày theo dự án.
 *
 * Ảnh vốn nằm rải trong từng dự án (mục Dự án), muốn xem «hôm qua cả công ty ra những thumbnail
 * nào» thì phải mở từng dự án một. Hai hàm này xếp lại cùng một dữ liệu theo hai chiều, không nhân
 * bản thêm bản ghi nào.
 */
export function groupThumbnailsByDay<T extends ThumbnailItem>(
  items: readonly T[],
): { dateKey: string; projects: { projectId: number; items: T[] }[]; total: number }[] {
  const days = new Map<string, Map<number, T[]>>();
  for (const item of items) {
    const projects = days.get(item.dateKey) ?? new Map<number, T[]>();
    const list = projects.get(item.projectId) ?? [];
    list.push(item);
    projects.set(item.projectId, list);
    days.set(item.dateKey, projects);
  }
  return [...days.entries()]
    .sort(([a], [b]) => byDayDesc(a, b))
    .map(([dateKey, projects]) => ({
      dateKey,
      projects: [...projects.entries()].map(([projectId, list]) => ({ projectId, items: list })),
      total: [...projects.values()].reduce((sum, list) => sum + list.length, 0),
    }));
}

/** Cùng dữ liệu, xếp theo DỰ ÁN rồi tới ngày — dự án nhiều ảnh nhất lên trước. */
export function groupThumbnailsByProject<T extends ThumbnailItem>(
  items: readonly T[],
): ThumbnailProjectDays<T>[] {
  const projects = new Map<number, Map<string, T[]>>();
  for (const item of items) {
    const days = projects.get(item.projectId) ?? new Map<string, T[]>();
    const list = days.get(item.dateKey) ?? [];
    list.push(item);
    days.set(item.dateKey, list);
    projects.set(item.projectId, days);
  }
  return [...projects.entries()]
    .map(([projectId, days]) => ({
      projectId,
      days: [...days.entries()]
        .sort(([a], [b]) => byDayDesc(a, b))
        .map(([dateKey, list]) => ({ dateKey, items: list })),
      total: [...days.values()].reduce((sum, list) => sum + list.length, 0),
    }))
    .sort((a, b) => b.total - a.total || a.projectId - b.projectId);
}

export function newProject(createdBy: string, now: Date): Project {
  return {
    recordId: 0,
    title: '',
    description: '',
    status: 'todo',
    startDate: null,
    deadline: null,
    channelCode: '',
    createdBy,
    createdAt: now.toISOString(),
    code: '',
    lead: createdBy,
    initialVideo: 0,
    initialThumb: 0,
    hoursPerUnit: 0,
    note: '',
    assignees: [],
    tasks: [],
    trackIds: [],
    videoIds: [],
    links: [],
  };
}

export function projectProblem(project: Pick<Project, 'title'>): string | null {
  return project.title.trim() ? null : 'Chưa đặt tên dự án';
}

/** Thời gian thực hiện hợp lệ: ngày bắt đầu không sau hạn hoàn thành (thiếu một đầu thì không xét). */
export function scheduleProblem(project: Pick<Project, 'startDate' | 'deadline'>): string | null {
  return project.startDate &&
    project.deadline &&
    dateOrdinal(project.startDate) > dateOrdinal(project.deadline)
    ? 'Ngày bắt đầu đang sau hạn hoàn thành'
    : null;
}

// ---------------------------------------------------------------- việc nhỏ

export function newTaskId(tasks: readonly ProjectTask[]): string {
  const used = new Set(tasks.map((task) => task.id));
  let n = tasks.length + 1;
  while (used.has(`t${n}`)) n += 1;
  return `t${n}`;
}

export function projectProgress(project: Pick<Project, 'tasks'>): {
  done: number;
  total: number;
  percent: number;
} {
  const total = project.tasks.length;
  const done = project.tasks.filter((task) => task.done).length;
  return { done, total, percent: total ? Math.round((done / total) * 100) : 0 };
}

// ---------------------------------------------------------------- hạn, thứ tự, lọc

export function isOverdue(
  project: Pick<Project, 'deadline' | 'status'>,
  today: CalendarDate,
): boolean {
  return (
    project.status !== 'done' &&
    project.deadline !== null &&
    dateOrdinal(project.deadline) < dateOrdinal(today)
  );
}

/** Số ngày còn lại tới hạn (âm = đã quá hạn); null khi không có hạn. */
export function daysToDeadline(
  project: Pick<Project, 'deadline'>,
  today: CalendarDate,
): number | null {
  return project.deadline ? daysBetween(today, project.deadline) : null;
}

export interface ProjectViewer {
  username: string;
  /** null = thấy tất cả (Giám đốc). */
  visibleUsernames: readonly string[] | null;
}

/** Người phụ trách hiện trên bảng: đã gán thì người đó, chưa thì người tạo. */
export function projectLead(project: Pick<Project, 'lead' | 'createdBy'>): string {
  return project.lead || project.createdBy;
}

/**
 * Ai thấy dự án: Giám đốc; người tạo; người phụ trách; người được giao (kể cả trong việc nhỏ); và cấp
 * trên nhìn được bất kỳ ai trong số đó (Trưởng phòng thấy dự án của phòng mình).
 */
export function canSeeProject(project: Project, viewer: ProjectViewer): boolean {
  if (viewer.visibleUsernames === null) return true;
  const involved = new Set([
    project.createdBy,
    projectLead(project),
    ...project.assignees,
    ...project.tasks.map((task) => task.who).filter(Boolean),
  ]);
  if (involved.has(viewer.username)) return true;
  const visible = new Set(viewer.visibleUsernames);
  return [...involved].some((username) => visible.has(username));
}

const STATUS_RANK: Record<ProjectStatus, number> = { doing: 0, todo: 1, done: 2 };
const byTitle = new Intl.Collator('vi', { sensitivity: 'base' });

/** Đang làm trước, chờ làm, rồi xong; trong mỗi nhóm hạn gần trước (không hạn xếp cuối), rồi theo tên. */
export function sortProjects(projects: readonly Project[]): Project[] {
  return [...projects].sort((a, b) => {
    const rank = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (rank) return rank;
    const da = a.deadline ? dateOrdinal(a.deadline) : Number.POSITIVE_INFINITY;
    const db = b.deadline ? dateOrdinal(b.deadline) : Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;
    return byTitle.compare(a.title, b.title) || a.recordId - b.recordId;
  });
}

export interface ProjectFilter {
  query: string;
  status: ProjectStatus | 'all';
  /** Chỉ dự án mình tạo, phụ trách hoặc được giao. */
  mine: boolean;
  username: string;
  /** Chỉ dự án do người này phụ trách; '' = tất cả. */
  lead?: string;
}

export function filterProjects(projects: readonly Project[], filter: ProjectFilter): Project[] {
  const needle = normalizeForSearch(filter.query);
  return projects.filter((project) => {
    if (filter.status !== 'all' && project.status !== filter.status) return false;
    if (filter.lead && projectLead(project) !== filter.lead) return false;
    if (
      filter.mine &&
      project.createdBy !== filter.username &&
      projectLead(project) !== filter.username &&
      !project.assignees.includes(filter.username) &&
      !project.tasks.some((task) => task.who === filter.username)
    ) {
      return false;
    }
    if (
      needle &&
      !normalizeForSearch(`${project.title} ${project.code} ${project.description}`).includes(
        needle,
      )
    )
      return false;
    return true;
  });
}

export interface ResourceCounts {
  images: number;
  tracks: number;
  videos: number;
  links: number;
}

export function resourceCounts(project: Project, imageCount: number): ResourceCounts {
  return {
    images: imageCount,
    tracks: project.trackIds.length,
    videos: project.videoIds.length,
    links: project.links.length,
  };
}

/** Link ngoài phải có giao thức http(s); người dùng gõ «youtube.com/…» thì tự thêm. */
export function normalizeLinkUrl(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  const withScheme = /^https?:\/\//i.test(text) ? text : `https://${text}`;
  try {
    const url = new URL(withScheme);
    return url.hostname.includes('.') ? url.toString() : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- tệp đính kèm (file gốc)

/**
 * Soát cỡ FILE GỐC trước khi gửi (07/10/2026, chủ dự án: «up project đã zip nhưng quá 200 MB nên web không
 * cho up»): trần do máy chủ gửi về cùng danh sách tệp (`PROJECT_FILE_MAX_MB`, mặc định 4 GB). Gửi trọn vài
 * GB rồi mới bị từ chối là phí cả buổi. 0 = chưa biết trần → không chặn, máy chủ vẫn là nơi quyết.
 */
export function attachmentSizeProblem(sizeBytes: number, maxFileBytes: number): string | null {
  if (sizeBytes <= 0) return 'File rỗng';
  if (maxFileBytes > 0 && sizeBytes > maxFileBytes) {
    return `File quá lớn — tối đa ${describeUploadLimit(maxFileBytes)}`;
  }
  return null;
}
